import React from "react";
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { StatusChip } from "./StatusChip";
import { ReferralBooking, ReferralPartner } from "../types/referrals";

interface PartnerDrawerProps {
  open: boolean;
  partner: ReferralPartner | null;
  bookings: ReferralBooking[];
  loadingBookings: boolean;
  bookingsError: string | null;
  onClose: () => void;
}

function getInitials(name: string, surname: string) {
  return `${name.charAt(0)}${surname.charAt(0)}`.toUpperCase();
}

function formatOrgCategory(cat: string) {
  return cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCurrency(amount: number | undefined | null) {
  if (amount == null) return "—";
  return `$${amount.toFixed(2)}`;
}

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
    <Box
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        minWidth: 20,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} noWrap>
        {value}
      </Typography>
    </Box>
  </Box>
);

export const PartnerDrawer: React.FC<PartnerDrawerProps> = ({
  open,
  partner,
  bookings,
  loadingBookings,
  bookingsError,
  onClose,
}) => {
  if (!partner) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 440,
          maxWidth: "100vw",
          borderLeft: "1px solid",
          borderColor: "divider",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.06)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          bgcolor: "background.paper",
          zIndex: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600} fontSize="1rem">
          Partner details
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {/* Profile */}
        <Box
          sx={{ px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2 }}
        >
          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: "primary.light",
              color: "primary.main",
              fontWeight: 700,
              fontSize: "1rem",
            }}
          >
            {getInitials(partner.name, partner.surname)}
          </Avatar>
          <Box>
            <Typography fontWeight={600} fontSize="1rem">
              {partner.name} {partner.surname}
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            >
              <StatusChip type="partner" status={partner.status} />
              <StatusChip
                type="commission"
                status={partner.commission_status}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Contact info */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing="0.05em"
            display="block"
            mb={1}
          >
            Contact information
          </Typography>
          <InfoRow
            icon={<EmailOutlinedIcon sx={{ fontSize: 16 }} />}
            label="Email"
            value={partner.email}
          />
          <InfoRow
            icon={<PhoneOutlinedIcon sx={{ fontSize: 16 }} />}
            label="Phone"
            value={partner.phone_number}
          />
          <InfoRow
            icon={<BusinessOutlinedIcon sx={{ fontSize: 16 }} />}
            label="Organisation"
            value={formatOrgCategory(partner.organisation_category)}
          />
        </Box>

        <Divider />

        {/* Commission details */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing="0.05em"
            display="block"
            mb={1}
          >
            Commission details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              mt: 1,
            }}
          >
            {[
              {
                label: "Commission rate",
                value: `${partner.commission_rate_applied}%`,
              },
              {
                label: "Commission amount",
                value: formatCurrency(partner.commission_amount),
              },
              {
                label: "Reward paid",
                value: partner.reward_paid ? (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 14, color: "success.main" }}
                    />
                    <span>Yes</span>
                  </Stack>
                ) : (
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <CancelOutlinedIcon
                      sx={{ fontSize: 14, color: "error.main" }}
                    />
                    <span>No</span>
                  </Stack>
                ),
              },
              {
                label: "Paid at",
                value: partner.reward_paid_at
                  ? new Date(partner.reward_paid_at).toLocaleDateString()
                  : "—",
              },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} mt={0.25}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Bookings */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            textTransform="uppercase"
            letterSpacing="0.05em"
            display="block"
            mb={1.5}
          >
            Bookings
          </Typography>

          {loadingBookings ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : bookingsError ? (
            <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
              {bookingsError}
            </Alert>
          ) : bookings.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 2, textAlign: "center" }}
            >
              No bookings found for this partner.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Event", "Date", "Amount", "Status"].map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        color: "text.secondary",
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow
                    key={b.id}
                    sx={{ "&:last-child td": { borderBottom: 0 } }}
                  >
                    <TableCell>
                      <Tooltip title={b.event_title} placement="top">
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          sx={{ maxWidth: 140, display: "block" }}
                        >
                          {b.event_title}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(b.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {formatCurrency(b.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip type="booking" status={b.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
